import { Injectable } from '@angular/core';
import { GatewayResponse } from '@dave/types';
import { User2CustomerGetRequestPaged } from '@dave/types/dist/proto/auth/user2Customer';
import { GetEventRequest } from '@dave/types/dist/proto/history/event';
import { Action, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, from, of, Subject } from 'rxjs';
import { catchError, concatMap, map, take } from 'rxjs/operators';
import { stringifyIfNotNullOrUndefined } from '../../helper/helper';
import { EventEntity, EventEntityFromGRPCBackend } from '../entities/event.entity';
import {
    UserToCustomerEntity,
    UserToCustomerEntityFromBackend,
    UserToCustomerEntityFromGRPCBackend,
} from '../entities/user-to-customer.entity';
import { State } from '../State';
import { BaseActionTypes } from '../State/actions/base.actions';
import { getToken } from '../State/selectors/base.selectors';
import { AppGatewayService } from './app-gateway.service';
import { HttpService } from './http.service';

enum ErrorCodes {
    Load = 'UserToCustomer Abrufen fehlgeschlagen',
    Remove = 'UserToCustomer Löschen fehlgeschlagen',
    Add = 'UserToCustomer Hinzufügen fehlgeschlagen',
}


@Injectable({
    providedIn: 'root',
})
export class UserToCustomerDataService {
    protected cache = new BehaviorSubject<UserToCustomerEntity[]>([]);
    protected fetchedCustomers: number[] = [];

    constructor(
        private store$: Store<State>,
        private gatewayService: AppGatewayService,
        private gatewayHttpService: HttpService,
    ) {
    }

    GetFromCustomer$(customerId: number): Observable<UserToCustomerEntity[]> {
        if (this.fetchedCustomers.includes(customerId)) {
            return this.cache.pipe(map(c => c.filter(c => c.CustomerId === customerId)));
        } else {
            this.fetchedCustomers.push(customerId);
            return combineLatest([
                this.cache.pipe(map(c => c.filter(c => c.CustomerId === customerId))),
                from(new Promise<UserToCustomerEntity[]>((resolve, reject) => {
                    this.GetFromCustomer(customerId).then(res => {
                        this.cache.next([...this.cache.value, ...res]);
                        resolve(res);
                    }).catch(err => {
                        reject(err);
                    });
                }))
            ]).pipe(map(([cached, _]) => cached))
        }
    }

    GetFromCustomer(customerId: number): Promise<UserToCustomerEntity[]> {
        let req ={
            CustomerId: customerId.toString()
        } as User2CustomerGetRequestPaged;
        return new Promise((resolve, reject) => {
            let result: UserToCustomerEntity[] = [];

            let handleResult = function(res:GatewayResponse) {
                let resUserToCustomers = res.AuthUser2CustomerGetPaged.User2CustomerResponses;
                result.push(...resUserToCustomers.map(u2c => UserToCustomerEntityFromGRPCBackend(u2c)));
                if (res.AuthUser2CustomerGetPaged.LastPage) {
                    resolve(result);
                    return;
                } else {
                    req.OffsetId = resUserToCustomers[resUserToCustomers.length - 1].Id;
                    this.gatewayService.Request({
                        AuthUser2CustomerGetPaged: req
                    }).then(handleResult.bind(this)).catch(err => {
                        this.store$.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Load } }));
                        reject(err)
                    })
                }
            }

            this.gatewayService.Request({
                AuthUser2CustomerGetPaged: req
            }).then(handleResult.bind(this)).catch(err => {
                this.store$.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Load } }));
                reject(err)
            })
        })
    }

    Add(Payload: { customerId: number; userId: number; roleId: number }): Promise<UserToCustomerEntity[]> {
        return new Promise((resolve, reject) => {
            this.store$.select(getToken).pipe(take(1)).subscribe(token => {
                const queryString = `
                mutation {
                    createUser2Customer(
                        ${stringifyIfNotNullOrUndefined(Payload, 'userId')}
                        ${stringifyIfNotNullOrUndefined(Payload, 'customerId')}
                        ${stringifyIfNotNullOrUndefined(Payload, 'roleId')}
                    ) {
                        ${UserToCustomerEntity.GQLFields.join(',')}
                    }
                }`;

                this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(take(1)).subscribe(res => {
                    if (res && res.createUser2Customer) {
                        let change: UserToCustomerEntity[] = [];
                        let add: UserToCustomerEntity[] = [];
                        let inIdMap = this.cache.value.map((v) => v.Id);
                        res.createUser2Customer.forEach((vr) => {
                            let v = UserToCustomerEntityFromBackend(vr);
                            if (inIdMap.includes(v.Id)) {
                                change.push(v);
                            } else {
                                add.push(v);
                            }
                        });
                        let newCache = this.cache.value;
                        newCache.map(i => change.map(c => c.Id).includes(i.Id) ? change.find(c => c.Id === i.Id) : i);
                        newCache.push(...add);
                        this.cache.next(newCache);
                        resolve([...change, ...add]);
                    } else {
                        this.store$.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Add } }));
                        reject(ErrorCodes.Add);
                    }
                });
            });
        });
    }

    Remove(id: number): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.store$.select(getToken).pipe(take(1)).subscribe(token => {
                const queryString = `
                mutation {
                    deleteUser2Customer(
                        id: ${id}
                    ) {
                        ${UserToCustomerEntity.GQLFields.join(',')}
                    }
                }`;
                this.gatewayHttpService.graphQl({ query: queryString }, { token }).pipe(take(1)).subscribe(res => {
                    if (res && res.deleteUser2Customer) {
                        this.cache.next(this.cache.value.filter(i => i.Id !== id));
                        resolve();
                    } else {
                        this.store$.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Remove } }));
                        reject(ErrorCodes.Remove);
                    }
                });
            });
        });

    }

}
